import React, { useState } from 'react';
import { Search, MapPin, DollarSign, Bed, Car, ShoppingCart, Building2 } from 'lucide-react';
import { useProperty } from '../context/PropertyContext';
import { SearchFilters } from '../types';

interface SearchFormProps {
  onSearch?: () => void;
  compact?: boolean;
}

const SearchForm: React.FC<SearchFormProps> = ({ onSearch, compact = false }) => {
  const { searchFilters, setSearchFilters } = useProperty();
  
  const [localFilters, setLocalFilters] = useState<SearchFilters>(searchFilters);

  // Sincronizar localFilters com searchFilters quando eles mudarem
  React.useEffect(() => {
    setLocalFilters(searchFilters);
  }, [searchFilters]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchFilters(localFilters);
    onSearch?.();
  };

  const handleInputChange = (field: keyof SearchFilters, value: string | number) => {
    const newFilters = {
      ...localFilters,
      [field]: value
    };
    setLocalFilters(newFilters);
    
    // Aplicar filtros imediatamente no modo compacto (homepage)
    if (compact) {
      setSearchFilters(newFilters);
    }
  };

  if (compact) {
    return (
      <div className="bg-white rounded-2xl shadow-2xl p-6" style={{ width: '80vw', margin: '0 auto' }}>
        <form onSubmit={handleSubmit}>
          {/* Layout horizontal largo */}
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Dropdown Comprar/Alugar */}
            <div className="relative flex items-center">
              <ShoppingCart className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={localFilters.transactionType}
                onChange={(e) => handleInputChange('transactionType', e.target.value)}
                className="w-full lg:w-48 pl-10 pr-4 py-4 border border-gray-200 hover:border-gray-400 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-700 text-sm bg-white appearance-none cursor-pointer h-14 transition-all duration-200"
              >
                <option value="">Comprar ou alugar</option>
                <option value="Venda">Comprar</option>
                <option value="Aluguel">Alugar</option>
              </select>
            </div>
            
            {/* Dropdown Tipo de Imóvel */}
            <div className="relative flex items-center">
              <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={localFilters.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                className="w-full lg:w-60 pl-10 pr-4 py-4 border border-gray-200 hover:border-gray-400 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-700 text-sm bg-white appearance-none cursor-pointer h-14 transition-all duration-200"
              >
                <option value="">Tipo de imóvel</option>
                <optgroup label="Residencial">
                  <option value="Residential / Apartment">Apartamento</option>
                  <option value="Residential / Home">Casa</option>
                  <option value="Residential / Condominium">Casa de Condomínio</option>
                  <option value="Residential / Penthouse">Cobertura</option>
                  <option value="Residential / Flat">Flat</option>
                  <option value="Residential / Studio">Studio</option>
                  <option value="Residential / Loft">Loft</option>
                  <option value="Residential / Duplex">Duplex</option>
                  <option value="Residential / Triplex">Triplex</option>
                  <option value="Residential / Quadruplex">Quadruplex</option>
                  <option value="Residential / Village House">Casa de Vila</option>
                  <option value="Residential / Farm">Chácara</option>
                  <option value="Residential / Farm Ranch">Fazenda</option>
                  <option value="Residential / Sobrado">Sobrado</option>
                  <option value="Residential / Land Lot">Terreno</option>
                </optgroup>
                <optgroup label="Comercial">
                  <option value="Commercial / Building">Prédio</option>
                  <option value="Commercial / Office">Sala</option>
                  <option value="Commercial / Shop">Loja</option>
                  <option value="Commercial / Warehouse">Galpão</option>
                  <option value="Commercial / Industrial Shed">Barracão</option>
                  <option value="Commercial / Business Point">Ponto Comercial</option>
                  <option value="Commercial / Hotel">Hotel</option>
                  <option value="Commercial / Inn">Pousada</option>
                  <option value="Commercial / Land Lot">Terreno Comercial</option>
                  <option value="Commercial / Hotel">Hotel</option>
                  <option value="Commercial / Inn">Pousada</option>
                  <option value="Commercial / Land Lot">Terreno</option>
                </optgroup>
              </select>
            </div>
            
            {/* Campo de busca expandido */}
            <div className="relative flex-1 min-w-0 flex items-center">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Digite condomínio, região, bairro ou código do imóvel"
                value={localFilters.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="w-full pl-10 pr-4 py-4 border border-gray-200 hover:border-gray-400 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-700 text-lg h-14 transition-all duration-200"
              />
            </div>
            
            {/* Botão de busca */}
            <button
              type="submit"
              className="bg-orange-500 text-white px-8 py-4 rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center space-x-2 whitespace-nowrap lg:w-auto w-full text-base font-medium h-14"
            >
              <span>Buscar</span>
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-xl p-8" style={{ width: '80vw', margin: '0 auto' }}>
      <form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Transação
          </label>
          <div className="relative">
            <ShoppingCart className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
            <select
              value={localFilters.transactionType}
              onChange={(e) => handleInputChange('transactionType', e.target.value)}
              className="w-full pl-10 pr-4 py-4 border border-gray-300 hover:border-gray-500 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-gray-700 text-base transition-all duration-200"
            >
              <option value="">Comprar ou alugar</option>
              <option value="Venda">Comprar</option>
              <option value="Aluguel">Alugar</option>
            </select>
          </div>
        </div>


        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Localização
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
            <input
              type="text"
              placeholder="Bairro, cidade ou empreendimento"
              value={localFilters.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className="w-full pl-10 pr-4 py-4 border border-gray-300 hover:border-gray-500 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-gray-700 text-base transition-all duration-200"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tipo
          </label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
            <select
              value={localFilters.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              className="w-full pl-10 pr-4 py-4 border border-gray-300 hover:border-gray-500 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-gray-700 text-base transition-all duration-200"
            >
              <option value="">Tipo de imóvel</option>
              <optgroup label="Residencial">
                <option value="Residential / Apartment">Apartamento</option>
                <option value="Residential / Home">Casa</option>
                <option value="Residential / Condominium">Casa de Condomínio</option>
                <option value="Residential / Penthouse">Cobertura</option>
                <option value="Residential / Flat">Flat</option>
                <option value="Residential / Studio">Studio</option>
                <option value="Residential / Loft">Loft</option>
                <option value="Residential / Duplex">Duplex</option>
                <option value="Residential / Triplex">Triplex</option>
                <option value="Residential / Quadruplex">Quadruplex</option>
                <option value="Residential / Village House">Casa de Vila</option>
                <option value="Residential / Farm">Chácara</option>
                <option value="Residential / Farm Ranch">Fazenda</option>
                <option value="Residential / Sobrado">Sobrado</option>
                <option value="Residential / Land Lot">Terreno</option>
              </optgroup>
              <optgroup label="Comercial">
                <option value="Commercial / Building">Prédio</option>
                <option value="Commercial / Office">Sala</option>
                <option value="Commercial / Shop">Loja</option>
                <option value="Commercial / Warehouse">Galpão</option>
                <option value="Commercial / Industrial Shed">Barracão</option>
                <option value="Commercial / Business Point">Ponto Comercial</option>
                <option value="Commercial / Hotel">Hotel</option>
                <option value="Commercial / Inn">Pousada</option>
                <option value="Commercial / Land Lot">Terreno Comercial</option>
              </optgroup>
            </select>
          </div>
        </div>

        <div className="md:col-span-2 lg:col-span-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preço Mínimo
          </label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
            <input
              type="number"
              placeholder="0"
              value={localFilters.priceMin || ''}
              onChange={(e) => handleInputChange('priceMin', Number(e.target.value))}
              className="w-full pl-10 pr-4 py-4 border border-gray-300 hover:border-gray-500 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-gray-700 text-base transition-all duration-200"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preço Máximo
          </label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
            <input
              type="number"
              placeholder="10000000"
              value={localFilters.priceMax || ''}
              onChange={(e) => handleInputChange('priceMax', Number(e.target.value))}
              className="w-full pl-10 pr-4 py-4 border border-gray-300 hover:border-gray-500 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-gray-700 text-base transition-all duration-200"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quartos
          </label>
          <div className="relative">
            <Bed className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
            <select
              value={localFilters.bedrooms}
              onChange={(e) => handleInputChange('bedrooms', Number(e.target.value))}
              className="w-full pl-10 pr-4 py-4 border border-gray-300 hover:border-gray-500 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-gray-700 text-base transition-all duration-200"
            >
              <option value="0">Qualquer</option>
              <option value="1">1+</option>
              <option value="2">2+</option>
              <option value="3">3+</option>
              <option value="4">4+</option>
            </select>
          </div>
        </div>


        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Vagas
          </label>
          <div className="relative">
            <Car className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
            <select
              value={localFilters.parkingSpaces}
              onChange={(e) => handleInputChange('parkingSpaces', Number(e.target.value))}
              className="w-full pl-10 pr-4 py-4 border border-gray-300 hover:border-gray-500 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-gray-700 text-base transition-all duration-200"
            >
              <option value="0">Qualquer</option>
              <option value="1">1+</option>
              <option value="2">2+</option>
              <option value="3">3+</option>
              <option value="4">4+</option>
            </select>
          </div>
        </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          className="bg-orange-500 text-white px-10 py-4 rounded-lg hover:bg-orange-600 transition-colors flex items-center space-x-2 text-base font-medium"
        >
          <Search className="w-5 h-5" />
          <span>Buscar</span>
        </button>
      </div>
      </form>
    </div>
  );
};

export default SearchForm;